import { teamMembersApi } from '@/api/teamMembers';
import type {
    ActivateTeamMemberRequest,
    DeactivateTeamMemberRequest,
    TeamMemberDetailsRequest,
    TeamMemberResendInvitationRequest,
    TeamMemberRevokeInvitationRequest,
    TeamMembersListRequest,
    TeamMemberUpdateRoleRequest,
    UpdateTeamMemberRequest,
} from '@/types/api/teamMembers';
import type { Invitation, Role, User, UserStatus } from '@/types/models';
import {
    createEventHook,
    createSharedComposable,
    useMemoize,
} from '@vueuse/core';
import {
    useGet,
    usePatch,
    usePost,
    useToaster,
    useFilters,
} from '@foodics/ui-common';
import { computed, ref, watch } from 'vue';
import { onRoleUpdated } from './roles';
import { userStatusEnum } from '@/enums/userStatuses';
import { merge } from 'lodash-es';
import { useLocale } from '@/composables/locale';
import { parseError } from '@/utils/errors';
import { usePagination } from './usePagination';

export const teamMembersInjectionKey = Symbol('teamMembersInjectionKey');
export const {
    trigger: triggerToggleActivationStatus,
    on: onToggleActivationStatus,
} = createEventHook<string>();
const { trigger: triggerThrottleState, on: onStatusUnthrottled } =
    createEventHook<string>();

export const { trigger: triggerUpdateMember, on: onUpdateMember } =
    createEventHook<User>();

const { on: onInvitationRevoked, trigger: triggerInvitationRevoked } =
    createEventHook<{ userId: string }>();
const { on: onResendInvitation, trigger: triggerResendInvitation } =
    createEventHook<{ userId: string; invitation: Invitation }>();

type TeamMembersFilters = {
    is_owner: boolean;
    status: UserStatus[];
    roles: Role['id'][];
};

type TeamMembersOptions = {
    perPage?: number;
};

export const useTeamMembers = createSharedComposable(
    (options: TeamMembersOptions = {}) => {
        const { perPage } = options;

        const members = ref<User[]>([]);

        const { appliedFilters, setFilter } = useFilters<TeamMembersFilters>();

        const paginationParams = computed(() => ({
            include: 'roles,invitation',
            sort: '-created_at',
            ...(appliedFilters.value && {
                filter: appliedFilters.value,
            }),
            ...(perPage && {
                per_page: perPage,
            }),
        }));

        const fetchOptions = computed(() => ({
            params: paginationParams.value,
        }));

        const {
            items,
            isLoading,
            hasNextPage,
            fetchNextPage,
            hasFetchedInitialPage,
            resetPagination,
        } = usePagination<TeamMembersListRequest>(teamMembersApi.list, {
            fetchOptions: fetchOptions,
        });

        watch(items, () => {
            members.value.push(...items.value);
        });

        watch(
            appliedFilters,
            () => {
                members.value = [];
                resetPagination();
            },
            { deep: true },
        );

        onRoleUpdated(() => {
            members.value = [];
            resetPagination();
        });

        onToggleActivationStatus((userId) => {
            const member = members.value?.find((m) => m.id === userId);
            if (!member) return;

            handleActivation(member);
        });

        onStatusUnthrottled((userId) => {
            const member = members.value?.find((m) => m.id === userId);
            if (!member) return;
            handleUnthrottle(member);
        });

        onUpdateMember((member) => {
            const memberIdx = members.value?.findIndex(
                (m) => m.id === member.id,
            );
            if (!memberIdx || !members.value) return;

            members.value[memberIdx] = member;
        });

        onResendInvitation(({ userId, invitation }) => {
            const member = members.value?.find((m) => m.id === userId);
            if (!member) return;

            handleResendInvitation(member, invitation);
        });

        onInvitationRevoked(({ userId }) => {
            // Remove the user from the list if his invitation is revoked
            members.value = members.value.filter((m) => m.id !== userId);
        });

        return {
            members,
            isLoading,
            hasNextPage,
            fetchNextPage,
            hasFetchedInitialPage,
            appliedFilters,
            setFilter,
        };
    },
);

export const useTeamMember = useMemoize((id: string) => {
    const { addToast } = useToaster();
    const { t } = useLocale();
    const {
        data: member,
        isLoading: isLoadingMemberData,
        error: memberDataError,
    } = useGet<TeamMemberDetailsRequest['response']>(
        teamMembersApi.details(id),
    );

    const {
        execute: executeDeactivate,
        isLoading: isDeactivating,
        error: deactivateError,
    } = usePost<
        DeactivateTeamMemberRequest['payload'],
        DeactivateTeamMemberRequest['response']
    >(teamMembersApi.deactivate(id));

    async function deactivate() {
        try {
            const { error } = await executeDeactivate();

            if (error) throw error;

            addToast({
                title: t('settings.member_toasts.member_deactivated'),
                variant: 'success',
            });
            triggerToggleActivationStatus(id);
        } catch {
            addToast({
                title: t('settings.member_toasts.member_deactivate_error'),
                variant: 'error',
            });
        }
    }

    const {
        execute: executeActivate,
        isLoading: isActivating,
        error: activateError,
    } = usePost<
        ActivateTeamMemberRequest['payload'],
        ActivateTeamMemberRequest['response']
    >(teamMembersApi.activate(id));

    async function activate() {
        try {
            const { error } = await executeActivate();

            if (error) throw error;

            addToast({
                title: t('settings.member_toasts.member_activated'),
                variant: 'success',
            });
            triggerToggleActivationStatus(id);
        } catch {
            addToast({
                title: t('settings.member_toasts.member_activate_error'),
                variant: 'error',
            });
        }
    }

    const {
        execute: executeUnthrottle,
        isLoading: isUnthrottling,
        error: unthrottleError,
    } = usePost<
        ActivateTeamMemberRequest['payload'],
        ActivateTeamMemberRequest['response']
    >(teamMembersApi.unthrottle(id), {
        responseType: 'blob',
    });

    async function unthrottle() {
        try {
            const { error } = await executeUnthrottle();

            if (error) throw error;

            addToast({
                title: t('settings.member_toasts.member_unlocked'),
                variant: 'success',
            });
            triggerThrottleState(id);
        } catch {
            addToast({
                title: t('settings.member_toasts.member_unlock_error'),
                variant: 'error',
            });
        }
    }

    onToggleActivationStatus(() => {
        if (member.value?.data?.id !== id) return;
        handleActivation(member.value.data);
    });
    onStatusUnthrottled(() => {
        if (member.value?.data?.id !== id) return;
        handleUnthrottle(member.value.data);
    });
    onResendInvitation(({ invitation }) => {
        if (member.value?.data?.id !== id) return;
        handleResendInvitation(member.value.data, invitation);
    });

    onUpdateMember((updatedMember) => {
        if (updatedMember?.id !== member.value?.data?.id) return;
        member.value.data = merge({}, member.value.data, updatedMember);
    });

    return {
        member: computed(() => member.value?.data),
        isLoadingMemberData,
        memberDataError,
        deactivate,
        isDeactivating,
        deactivateError,
        activate,
        isActivating,
        activateError,
        unthrottle,
        isUnthrottling,
        unthrottleError,
        onInvitationRevoked,
    };
});

export function useUpdateTeamMember(
    id: string,
    opts: { showToaster?: boolean } = { showToaster: true },
) {
    const { addToast } = useToaster();
    const { t } = useLocale();

    const {
        execute: executeUpdate,
        isLoading: isUpdating,
        error: updateError,
    } = usePatch<
        UpdateTeamMemberRequest['payload'],
        UpdateTeamMemberRequest['response']
    >(teamMembersApi.update(id));

    async function update(payload: UpdateTeamMemberRequest['payload']) {
        try {
            const { error, data } = await executeUpdate(payload);
            if (error) throw error;
            if (!data)
                throw new Error(
                    t('settings.member_toasts.member_update_error'),
                );

            triggerUpdateMember(data.data);

            if (opts.showToaster) {
                addToast({
                    title: t('settings.member_toasts.member_updated'),
                    variant: 'success',
                });
            }
        } catch (err) {
            const message = parseError(
                err,
                t('settings.member_toasts.member_update_error'),
            );

            if (opts.showToaster) {
                addToast({ title: message, variant: 'error' });
            }
        }
    }

    return {
        update,
        isUpdating,
        updateError,
    };
}

export function useUpdateRole(memberId: string) {
    const { addToast } = useToaster();
    const { t } = useLocale();

    const {
        execute: executeUpdateRole,
        isLoading: isUpdatingRole,
        error: updateRoleError,
    } = usePost<
        TeamMemberUpdateRoleRequest['payload'],
        TeamMemberUpdateRoleRequest['response']
    >(teamMembersApi.updateRole(memberId));

    async function updateRole(payload: TeamMemberUpdateRoleRequest['payload']) {
        try {
            const { data, error } = await executeUpdateRole(payload);
            if (error) throw error;
            if (!data)
                throw new Error(
                    t('settings.member_toasts.member_update_role_error'),
                );

            triggerUpdateMember(data.data);
            addToast({
                title: t('settings.member_toasts.member_update_role_success'),
                variant: 'success',
            });

            return data.data;
        } catch (err) {
            const message = parseError(err);

            addToast({ title: message, variant: 'error' });
            throw err;
        }
    }

    return {
        updateRole,
        isUpdatingRole,
        updateRoleError,
    };
}

export function useRevokeInvitation(userId: string) {
    const { execute, isLoading, error, onData } = usePost<
        TeamMemberRevokeInvitationRequest['payload'],
        TeamMemberRevokeInvitationRequest['response']
    >(teamMembersApi.revokeInvitation(userId), {
        responseType: 'blob',
    });
    const { addToast } = useToaster();
    const { t } = useLocale();

    async function revoke() {
        try {
            const { error } = await execute();
            if (error) throw error;

            addToast({
                title: t('settings.member_toasts.invitation_revoked'),
                variant: 'success',
            });

            triggerInvitationRevoked({ userId });
        } catch (error) {
            addToast({
                title: parseError(error),
                variant: 'error',
            });
        }
    }

    return {
        revoke,
        isLoading,
        error,
        onInvitationRevoked: onData,
    };
}

export function useResendInvitation(userId: string) {
    const { execute, isLoading, error } = usePost<
        TeamMemberResendInvitationRequest['payload'],
        TeamMemberResendInvitationRequest['response']
    >(teamMembersApi.resendInvitation(userId));
    const { addToast } = useToaster();
    const { t } = useLocale();

    async function resend() {
        try {
            const { data, error } = await execute();
            if (error) throw error;
            if (!data?.data)
                throw new Error(
                    t('settings.member_toasts.member_invite_error'),
                );

            addToast({
                title: t('settings.member_toasts.invitation_sent'),
                variant: 'success',
            });

            triggerResendInvitation({ userId, invitation: data.data });
        } catch (err: unknown) {
            addToast({
                title: parseError(err),
                variant: 'error',
            });
        }
    }

    return {
        resend,
        isLoading,
        error,
    };
}

function handleUnthrottle(member: User) {
    member.is_throttled = false;
    member.status = userStatusEnum.Active;
}

function handleActivation(member: User) {
    if (Number(member.status) === userStatusEnum.Active) {
        member.status = userStatusEnum.Inactive;
    } else if (
        Number(member.status) === userStatusEnum.Inactive ||
        Number(member.status) === userStatusEnum.Locked
    ) {
        member.status = userStatusEnum.Active;
    }
}

function handleResendInvitation(member: User, invitation: Invitation) {
    member.invitation = merge({}, member.invitation, invitation);
    member.status = userStatusEnum.Invited;
}
